import { useEffect, useRef } from "react";

export type LivePollOptions = {
  /**
   * Milliseconds of no user interaction after which the poll falls back to
   * `idleIntervalMs`. Omit on kiosk surfaces (the projector is never touched,
   * so an interaction-driven backoff would just stop it going live).
   */
  idleAfterMs?: number;
  /** Cadence while idle. Only applies together with `idleAfterMs`. */
  idleIntervalMs?: number;
};

type DelayInput = {
  intervalMs: number;
  lastActivityAt: number;
  now: number;
} & LivePollOptions;

/** Cadence for the next tick. Pure, so the backoff rule is unit-testable. */
export const nextPollDelay = ({
  intervalMs,
  idleAfterMs,
  idleIntervalMs,
  lastActivityAt,
  now,
}: DelayInput): number => {
  if (!idleAfterMs || !idleIntervalMs) return intervalMs;
  if (now - lastActivityAt < idleAfterMs) return intervalMs;
  return Math.max(intervalMs, idleIntervalMs);
};

/**
 * Non-overlapping, visibility-aware polling — a safe replacement for Apollo's
 * `pollInterval`, which fires a fresh request every tick even if the last is
 * still in flight (so a slow response stacks and saturates the pool — the
 * poker polling death-spiral). This waits for each refetch to settle before
 * scheduling the next, and skips while the tab is hidden.
 *
 * Callers gate on relevance: pass `active: false` (or `intervalMs: 0`) for a
 * surface whose data can no longer change, and a slower `intervalMs` for one
 * that changes rarely. With `idleAfterMs`/`idleIntervalMs`, a tab left open
 * untouched steps down to the slower cadence and snaps back — with an
 * immediate refetch — the moment it is touched or refocused.
 */
export function useLivePoll(
  refetch: () => Promise<unknown>,
  intervalMs: number,
  active = true,
  { idleAfterMs, idleIntervalMs }: LivePollOptions = {},
): void {
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  useEffect(() => {
    if (!active || intervalMs <= 0) return undefined;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let lastActivityAt = Date.now();
    let idling = false;
    let inFlight = false;

    const schedule = () => {
      if (cancelled) return;
      const delay = nextPollDelay({
        intervalMs,
        idleAfterMs,
        idleIntervalMs,
        lastActivityAt,
        now: Date.now(),
      });
      idling = delay !== intervalMs;
      timer = setTimeout(run, delay);
    };

    const run = async () => {
      if (typeof document === "undefined" || !document.hidden) {
        inFlight = true;
        try {
          await refetchRef.current();
        } catch {
          /* transient — the next tick retries */
        }
        inFlight = false;
      }
      schedule();
    };

    const poke = (immediate: boolean) => {
      lastActivityAt = Date.now();
      if (cancelled || inFlight || !immediate) return;
      if (timer) clearTimeout(timer);
      void run();
    };

    // Refocusing a backed-off tab must not leave the user staring at data from
    // a minute ago, so becoming visible always refetches now.
    const onVisibility = () => {
      if (!document.hidden) poke(true);
    };
    const onInteraction = () => poke(idling);

    document.addEventListener("visibilitychange", onVisibility);
    if (idleAfterMs) {
      window.addEventListener("focus", onVisibility);
      window.addEventListener("pointerdown", onInteraction);
      window.addEventListener("keydown", onInteraction);
    }

    schedule();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onVisibility);
      window.removeEventListener("pointerdown", onInteraction);
      window.removeEventListener("keydown", onInteraction);
    };
  }, [intervalMs, active, idleAfterMs, idleIntervalMs]);
}
